/**
 * EntityBelege — the object view's Belege with FR-017.7's add and unlink.
 *
 * The tiles come from the Belege read (`useEntitySourceDocs`), which is keyed
 * by document and carries no link ids; the ids come from this object's own
 * link rows (`GET /twin/links?target_type&target_id`). A tile the facts alone
 * put there has no row and so no unlink — there is nothing to supersede.
 *
 * Editing is offered in the LIVE view only: the as-of view shows the object
 * as it was, and a link written from there would be written now.
 */
import React from 'react';

import { useRefreshLinkQueries, useSupersedeLink, useTargetLinks } from '../../../hooks/useTwinLinks';
import { documentService } from '../../../services/documentService';
import type { DocumentLink, EntitySourceDoc } from '../../../services/twinService';
import AddEntityLinkForm, { type DocumentChoice } from './AddEntityLinkForm';
import DocumentsPanel from './DocumentsPanel';

/** Archive search for the picker: filename, smart filename, description, tags. */
export async function searchDocumentsForLink(q: string): Promise<DocumentChoice[]> {
    const { documents } = await documentService.listDocuments({ search: q, limit: 8 });
    return documents.map((d) => ({
        document_id: d.doc_id,
        label: d.smart_filename || d.filename,
        document_date: d.document_date,
    }));
}

interface EntityBelegeProps {
    entityType: string;
    entityId: string;
    live: boolean;
    docs?: EntitySourceDoc[];
    isLoading?: boolean;
    isError?: boolean;
    onPreview?: (docId: string) => void;
}

export const EntityBelege: React.FC<EntityBelegeProps> = ({
    entityType,
    entityId,
    live,
    docs,
    isLoading,
    isError,
    onPreview,
}) => {
    const refreshLinks = useRefreshLinkQueries();
    const [adding, setAdding] = React.useState(false);

    const { data: links } = useTargetLinks(entityType, entityId, live);

    const linksByDoc = React.useMemo(() => {
        const byDoc = new Map<string, DocumentLink[]>();
        for (const link of links ?? []) {
            const bucket = byDoc.get(link.document_id);
            if (bucket) bucket.push(link);
            else byDoc.set(link.document_id, [link]);
        }
        return byDoc;
    }, [links]);

    const unlink = useSupersedeLink();

    return (
        <>
            <DocumentsPanel
                docs={docs}
                isLoading={isLoading}
                isError={isError}
                onPreview={onPreview}
                linksByDoc={live ? linksByDoc : undefined}
                onUnlink={live ? (linkId) => unlink.mutateAsync(linkId) : undefined}
            />
            {live &&
                (adding ? (
                    <AddEntityLinkForm
                        entityType={entityType}
                        entityId={entityId}
                        searchDocuments={searchDocumentsForLink}
                        onCreated={() => {
                            setAdding(false);
                            refreshLinks();
                        }}
                        onCancel={() => setAdding(false)}
                    />
                ) : (
                    <button type="button" className="documents-panel__add-toggle" onClick={() => setAdding(true)}>
                        Beleg verknüpfen
                    </button>
                ))}
        </>
    );
};

export default EntityBelege;
